import React, { useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faChevronUp,
	faChevronDown,
	faTimes,
} from "@fortawesome/free-solid-svg-icons"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"
import { round100 } from "../../../utilities/Addons"
import CommandeHeader from "./CommandeHeader"
import AnnulationCommandeVirtuelleModalV2 from "./AnnulationCommandeVirtuelleModalV2"

// Prix : toujours 2 décimales (laisse la valeur telle quelle si non numérique).
const fmt2 = (v) => {
	const n = Number(v)
	return Number.isNaN(n) ? v : n.toFixed(2)
}

// Carte d'une commande VIRTUELLE (onglet Commandes Virtuelles).
// Données distinctes des commandes normales : en-tête fournisseur + date +
// référence + n°, et tableau des articles (label, numéro, quantités reçue /
// restante / rendue, prix achat & vente HT). Affichage seul, repliable — comme
// l'ancien `AtelierInventaireCommande isVirtuelle`.
// GE-55 : annulation logique (rupture fournisseur) — badge « Annulée » +
// motif / qui / quand, « Rétablir ».
// GE-79 (Jordan 30.10) : onglet HISTORIQUE — les boutons d'annulation sont
// dans Stock Virtuel ; ici, Rétablir (commande et ligne annulée, barrée). Seule
// exception : une commande perso (absente du stock virtuel) s'annule ici,
// commande et lignes.
// Revue 30.10 (G1) : `onModifie` après une annulation / un rétablissement
// (Stock Virtuel rechargé, la ligne y revient ou en sort).
const CommandeVirtuelleCard = ({ commande, onModifie }) => {
	const [open, setOpen] = useState(true)
	// null | { retablir, article } (article null = la commande)
	const [modale, setModale] = useState(null)
	const annulee = !!commande.annulee_le
	const perso = !!commande.perso_employe
	const articles = commande.all_articles || []
	const total = round100(
		articles
			.filter((a) => !a.annule_le)
			.reduce((s, a) => s + a.quantite_recue * a.prix_achat_ht, 0),
	)
	const ouvrir = (retablir, article = null) =>
		setModale({ retablir, article })

	return (
		<div style={cs.card}>
			<CommandeHeader
				fournisseur={commande.fournisseur_nom}
				date={commande.date_commande}
				reference={commande.reference}
				numero={commande.numero_commande}
				total={total}
				totalLabel="CHF HT"
				perso={commande.perso_employe}
				actions={
					<>
						{annulee ? (
							<>
								<span style={cs.badgeAnnulee}>Annulée</span>
								<button
									type="button"
									style={cs.action}
									onClick={() => ouvrir(true)}
								>
									Rétablir
								</button>
							</>
						) : (
							perso && (
								<button
									type="button"
									style={
										commande.annulable
											? cs.iconDanger
											: {
													...cs.iconDanger,
													...cs.iconOff,
												}
									}
									disabled={!commande.annulable}
									aria-label="Annuler la commande"
									title={
										commande.annulable
											? "Annuler la commande"
											: commande.raison_non_annulable
									}
									onClick={() => ouvrir(false)}
								>
									<FontAwesomeIcon icon={faTimes} />
								</button>
							)
						)}
						<button
							type="button"
							style={cs.toggle}
							onClick={() => setOpen((o) => !o)}
							title={open ? "Replier" : "Déplier"}
						>
							<FontAwesomeIcon
								icon={open ? faChevronUp : faChevronDown}
							/>
						</button>
					</>
				}
			/>

			{annulee && (
				<div style={cs.bandeau}>
					Annulée le {commande.annulee_le}
					{commande.annulee_par_nom
						? ` par ${commande.annulee_par_nom}`
						: ""}{" "}
					— Motif : {commande.motif_annulation}
				</div>
			)}
			{modale && (
				<AnnulationCommandeVirtuelleModalV2
					commande={commande}
					article={modale.article}
					retablir={modale.retablir}
					onClose={() => setModale(null)}
					onDone={onModifie}
				/>
			)}

			{open && (
				<div style={cs.tableWrap}>
					<table style={cs.table}>
						{/* Largeurs fixes → colonnes alignées sur toutes les cartes */}
						<colgroup>
							<col style={{ width: "5%" }} />
							<col style={{ width: "21%" }} />
							<col style={{ width: "12%" }} />
							<col style={{ width: "10%" }} />
							<col style={{ width: "10%" }} />
							<col style={{ width: "10%" }} />
							<col style={{ width: "10%" }} />
							<col style={{ width: "10%" }} />
							<col style={{ width: "12%" }} />
						</colgroup>
						<thead>
							<tr>
								<th style={cs.th}>N°</th>
								<th style={cs.th}>Article</th>
								<th style={cs.th}>Numéro</th>
								<th style={cs.th}>Qté reçue</th>
								<th style={cs.th}>Qté restante</th>
								<th style={cs.th}>Qté rendue</th>
								<th style={cs.thRight}>Prix achat HT</th>
								<th style={cs.thRight}>Prix vente HT</th>
								<th style={cs.th} aria-label="Actions" />
							</tr>
						</thead>
						<tbody>
							{articles.map((a, i) => (
								<tr
									key={`${a.id}-${i}`}
									style={{
										backgroundColor:
											i % 2 === 1
												? COLORS.bg
												: COLORS.surface,
										...(a.annule_le ? cs.ligneAnnulee : {}),
									}}
									title={
										a.annule_le
											? `Annulée le ${a.annule_le}${
													a.annule_par_nom
														? ` par ${a.annule_par_nom}`
														: ""
												} — Motif : ${a.motif_annulation}`
											: undefined
									}
								>
									<td style={cs.td} title={String(i + 1)}>
										{i + 1}
									</td>
									<td
										style={{ ...cs.td, fontWeight: 600 }}
										title={
											a.annule_le ? undefined : a.label
										}
									>
										{a.label}
									</td>
									<td style={cs.td} title={a.numero}>
										{a.numero}
									</td>
									<td
										style={cs.td}
										title={String(a.quantite_recue)}
									>
										{a.quantite_recue}
									</td>
									<td
										style={cs.td}
										title={String(
											a.quantite_recue -
												a.quantite_utilisee,
										)}
									>
										{a.quantite_recue - a.quantite_utilisee}
									</td>
									<td
										style={cs.td}
										title={String(a.quantite_rendue)}
									>
										{a.quantite_rendue}
									</td>
									<td
										style={cs.tdRight}
										title={fmt2(a.prix_achat_ht)}
									>
										{fmt2(a.prix_achat_ht)}
									</td>
									<td
										style={cs.tdRight}
										title={fmt2(a.prix_vente_ht)}
									>
										{fmt2(a.prix_vente_ht)}
									</td>
									<td style={cs.tdActions}>
										{ActionLigne(
											commande,
											a,
											annulee,
											perso,
											ouvrir,
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</div>
	)
}

// Cellule d'action d'une ligne : badge « Annulée » + Rétablir (commande non
// annulée), ✕ seulement pour une commande perso (GE-79). Fonction module-level
// (piège CRA 3 : branches dans le render).
const ActionLigne = (commande, a, annulee, perso, ouvrir) => {
	if (a.annule_le)
		return (
			<span style={cs.actionsLigne}>
				<span style={cs.badgeAnnulee}>Annulée</span>
				{!annulee && (
					<button
						type="button"
						style={cs.actionPetite}
						onClick={() => ouvrir(true, a)}
					>
						Rétablir
					</button>
				)}
			</span>
		)
	if (!perso || annulee) return null
	return (
		<button
			type="button"
			style={
				a.annulable
					? cs.iconDanger
					: { ...cs.iconDanger, ...cs.iconOff }
			}
			disabled={!a.annulable}
			aria-label="Annuler la ligne"
			title={a.annulable ? "Annuler la ligne" : a.raison_non_annulable}
			onClick={() => ouvrir(false, a)}
		>
			<FontAwesomeIcon icon={faTimes} />
		</button>
	)
}

const cs = {
	card: {
		backgroundColor: COLORS.surface,
		borderRadius: 14,
		boxShadow:
			"0 1px 2px rgba(28, 25, 23, 0.04), 0 6px 20px rgba(28, 25, 23, 0.06)",
		marginBottom: 16,
		overflow: "hidden",
	},
	toggle: {
		width: 32,
		height: 32,
		borderRadius: 8,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		color: COLORS.textMuted,
		cursor: "pointer",
		fontSize: 13,
	},
	action: {
		height: 32,
		padding: "0 12px",
		borderRadius: 8,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		color: COLORS.text,
		cursor: "pointer",
		fontSize: 13,
		whiteSpace: "nowrap",
	},
	// Destructeur icône seule : couleurs de generalStyles.btnDanger, gabarit
	// du bouton replier / déplier (32 × 32).
	iconDanger: {
		...gs.btnDanger,
		width: 32,
		height: 32,
		padding: 0,
		borderRadius: 8,
		fontSize: 14,
	},
	iconOff: {
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		color: COLORS.textLight,
		cursor: "not-allowed",
	},
	badgeAnnulee: {
		// Centrée dans la rangée d'actions (flex sans alignItems : les boutons
		// de 32 px l'étiraient, texte collé en haut).
		alignSelf: "center",
		display: "inline-flex",
		alignItems: "center",
		height: 24,
		padding: "0 10px",
		lineHeight: 1,
		boxSizing: "border-box",
		borderRadius: 999,
		border: `1px solid ${COLORS.danger}`,
		fontSize: 12,
		fontWeight: 600,
		color: COLORS.danger,
		backgroundColor: COLORS.dangerLight,
		whiteSpace: "nowrap",
	},
	bandeau: {
		padding: "10px 18px",
		fontSize: 13,
		color: COLORS.danger,
		backgroundColor: COLORS.dangerLight,
		borderBottom: `1px solid ${COLORS.border}`,
	},
	// GE-79 : ligne annulée barrée (le badge et le bouton ne le sont pas).
	ligneAnnulee: { textDecoration: "line-through" },
	actionsLigne: {
		display: "inline-flex",
		alignItems: "center",
		gap: 6,
		textDecoration: "none",
	},
	actionPetite: {
		height: 26,
		padding: "0 10px",
		borderRadius: 8,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		color: COLORS.text,
		cursor: "pointer",
		fontSize: 12,
		whiteSpace: "nowrap",
	},
	tdActions: {
		padding: "6px 12px",
		border: "none",
		textAlign: "right",
		verticalAlign: "middle",
		whiteSpace: "nowrap",
	},
	tableWrap: { overflowX: "auto" },
	table: {
		width: "100%",
		minWidth: 760,
		tableLayout: "fixed",
		borderCollapse: "separate",
		borderSpacing: 0,
		fontSize: 13,
	},
	th: {
		padding: "10px 16px",
		textAlign: "left",
		fontSize: 11,
		fontWeight: 600,
		color: COLORS.textLight,
		textTransform: "uppercase",
		letterSpacing: "0.06em",
		border: "none",
		borderBottom: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.bg,
		whiteSpace: "nowrap",
	},
	thRight: {
		padding: "10px 16px",
		textAlign: "right",
		fontSize: 11,
		fontWeight: 600,
		color: COLORS.textLight,
		textTransform: "uppercase",
		letterSpacing: "0.06em",
		border: "none",
		borderBottom: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.bg,
		whiteSpace: "nowrap",
	},
	td: {
		padding: "10px 16px",
		border: "none",
		textAlign: "left",
		color: COLORS.text,
		verticalAlign: "middle",
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	tdRight: {
		padding: "10px 16px",
		border: "none",
		textAlign: "right",
		color: COLORS.text,
		verticalAlign: "middle",
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
		fontVariantNumeric: "tabular-nums",
	},
}

export default CommandeVirtuelleCard
